import { cn } from '@/lib/cn';
import { strings } from '@/strings';

/**
 * The wordmark, in one place so every appearance stays the same asset at the
 * same aspect ratio.
 *
 * The Arabic wordmark is the default because the interface is Arabic; the
 * Latin one exists for the few places a Latin lockup belongs (an English
 * export, a Latin-script document header) rather than as a language toggle.
 *
 * `alt` is the app name rather than "logo" — a screen reader should announce
 * أريب, which is the information the image carries. Width and height are set
 * so the layout does not jump while the PNG loads.
 *
 * `withTagline` sets the motto under the wordmark. The lockup — mark, gap,
 * motto — lives here rather than at each call site so the sidebar and the
 * sign-in page cannot drift apart.
 *
 * Size is a named step, not a free class, because the mark and the motto have
 * to grow together: a bigger wordmark over an unchanged 11px motto reads as a
 * mistake. `className` is then only ever about how the lockup sits in its
 * parent, which is the one thing that really does differ per call site.
 */
const SIZES = {
  sm: { img: 'h-7', tagline: 'text-[11px]', gap: 'gap-1' },
  lg: { img: 'h-20', tagline: 'text-base', gap: 'gap-2.5' },
} as const;

export function Logo({
  variant = 'ar',
  size = 'sm',
  withTagline = false,
  className,
}: {
  variant?: 'ar' | 'en';
  size?: keyof typeof SIZES;
  withTagline?: boolean;
  className?: string;
}) {
  const step = SIZES[size];
  const src = variant === 'ar' ? '/logo-ar.png' : '/logo-en.png';

  return (
    <span className={cn('inline-flex flex-col', step.gap, className)}>
      <img
        src={src}
        alt={strings.appName}
        width={900}
        height={variant === 'ar' ? 272 : 228}
        className={cn(step.img, 'w-auto')}
      />
      {withTagline ? (
        <span className={cn(step.tagline, 'tracking-wide text-muted')}>{strings.tagline}</span>
      ) : null}
    </span>
  );
}
